/**
 * Retroactive shift detection hook — checks for missed clock-in/out on app open.
 *
 * Architecture:
 *   - Calls GET /api/geo/retroactive-check ONCE on mount
 *   - Re-checks on visibilitychange → 'visible' (debounced: skip if checked <5 min ago)
 *   - Uses sessionStorage to avoid re-showing dismissed overlays within same browser session
 *   - Returns the retroactive suggestion (if any) and accept/dismiss handlers
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────

export interface RetroactiveData {
  type: 'missed_clock_in' | 'missed_clock_out';
  station_name: string | null;
  suggested_time: string;
  session_id: number;
  shift_start: string | null;
  shift_end: string | null;
  message: string;
  confidence: number;
}

interface AutoActionResult {
  type: string;
  entry_id: number;
}

type RetroactiveResponse =
  | { retroactive: RetroactiveData; suggestion_id: number; auto_action?: undefined }
  | { retroactive: null; suggestion_id?: undefined; auto_action?: AutoActionResult | null };

export interface UseRetroactiveCheckReturn {
  retroactive: RetroactiveData | null;
  suggestionId: number | null;
  lastAutoAction: AutoActionResult | null;
  accept: (suggestionId: number, extra?: Record<string, string>, clockTime?: string) => Promise<boolean>;
  dismiss: (suggestionId: number) => Promise<boolean>;
  clearAutoAction: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────

const CHECK_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'retroactive_dismissed_';
const MAX_FAILURES = 3;
const FAILURE_BACKOFF_MS = 60 * 1000; // 1 minute per failure

// ── Hook ─────────────────────────────────────────────────────────────────

export function useRetroactiveCheck(): UseRetroactiveCheckReturn {
  const [retroactive, setRetroactive] = useState<RetroactiveData | null>(null);
  const [suggestionId, setSuggestionId] = useState<number | null>(null);
  const [lastAutoAction, setLastAutoAction] = useState<AutoActionResult | null>(null);
  const lastCheckRef = useRef<number>(0);
  const failCountRef = useRef(0);
  const lastFailRef = useRef<number>(0);

  const fetchCheck = useCallback(async () => {
    // Exponential backoff on repeated failures, but always allow retry eventually
    if (failCountRef.current >= MAX_FAILURES) {
      const backoff = FAILURE_BACKOFF_MS * failCountRef.current;
      if (Date.now() - lastFailRef.current < backoff) return;
    }

    const now = Date.now();
    if (now - lastCheckRef.current < CHECK_DEBOUNCE_MS) return;
    lastCheckRef.current = now;

    try {
      const data = await apiFetch<RetroactiveResponse>('/api/geo/retroactive-check');

      if (data.auto_action) {
        // Auto-clock fired retroactively — surface to UI as toast
        setLastAutoAction(data.auto_action);
        setRetroactive(null);
        setSuggestionId(null);
      } else if (data.retroactive && data.suggestion_id) {
        // Check if this was already dismissed in this browser session
        const key = `${STORAGE_PREFIX}${data.suggestion_id}`;
        if (sessionStorage.getItem(key)) {
          setRetroactive(null);
          setSuggestionId(null);
          return;
        }
        setRetroactive(data.retroactive);
        setSuggestionId(data.suggestion_id);
      } else {
        setRetroactive(null);
        setSuggestionId(null);
      }
    } catch {
      failCountRef.current += 1;
      lastFailRef.current = Date.now();
    }
  }, []);

  // Check on mount and visibility change
  useEffect(() => {
    fetchCheck();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchCheck]);

  const accept = useCallback(
    async (id: number, extra?: Record<string, string>, clockTime?: string): Promise<boolean> => {
      try {
        await apiFetch(`/api/geo/suggestion/${id}/respond`, {
          method: 'POST',
          body: JSON.stringify({ action: 'accept', ...extra, ...(clockTime ? { clock_time: clockTime } : {}) }),
        });
        setRetroactive(null);
        setSuggestionId(null);
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  const dismiss = useCallback(async (id: number): Promise<boolean> => {
    try {
      await apiFetch(`/api/geo/suggestion/${id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss' }),
      });
      // Persist dismissal in sessionStorage to avoid re-showing
      sessionStorage.setItem(`${STORAGE_PREFIX}${id}`, '1');
      setRetroactive(null);
      setSuggestionId(null);
      return true;
    } catch {
      return false;
    }
  }, []);

  const clearAutoAction = useCallback(() => setLastAutoAction(null), []);

  return { retroactive, suggestionId, lastAutoAction, accept, dismiss, clearAutoAction };
}
